import React, { useState } from 'react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { Plus } from 'lucide-react';

const faqs = [
  {
    question: 'How do we get started?',
    answer:
      "Book a free 30 minute call. We will ask about the problem you are trying to solve, what systems and tools you already use, and what a good result looks like. No preparation needed. Just talk us through it.",
  },
  {
    question: 'How much does a project typically cost?',
    answer:
      'Most focused projects start at around $1,500 to $3,000. Larger or more complex builds are scoped and priced individually. We always provide a fixed quote before anything starts. No open invoices, no surprise bills.',
  },
  {
    question: 'How long does it take to build something?',
    answer:
      'A focused tool typically takes 2 to 4 weeks from a confirmed brief to a live product. We give you a realistic timeline upfront and stick to it.',
  },
  {
    question: 'Do I own what you build?',
    answer:
      'Yes, fully. The finished product belongs to you. We can hand over the complete codebase if you want to take it elsewhere, or host and maintain it for you on an ongoing basis. Your choice.',
  },
  {
    question: 'What if I need changes after launch?',
    answer:
      'Almost every client wants to adjust something once they start using the tool in real life. We offer ongoing support for updates and new features, either on a monthly retainer or at an hourly rate.',
  },
  {
    question: 'What exactly is a sales prospecting app?',
    answer:
      "It is a custom tool built specifically for your sales team. It pulls company data from one or more sources, filters by the criteria that matter to you (industry, size, geography, revenue, technology stack, whatever), and gives your team a clean, prioritised list of companies to reach out to. Instead of paying for a generic database full of irrelevant contacts, you get a tool built around exactly how your team sells.",
  },
  {
    question: 'Can you integrate with the tools we already use?',
    answer:
      "Yes. We build integrations with CRM systems, email, calendar tools, ERP systems, accounting software, and most custom databases. If you are using it and it has an API, we can most likely connect to it.",
  },
  {
    question: 'Do I need to understand the technical side?',
    answer:
      'Not at all. You explain the business problem, we handle everything technical. At every step we explain what we are building in plain language. No jargon, no technical assumptions.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

export const CommonQuestions = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="py-24 md:py-32 bg-[#0a0a0a] relative">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="container-professional">
        <div className="max-w-2xl mb-14 md:mb-16">
          <div className="text-[11px] font-semibold tracking-[0.2em] text-primary uppercase mb-4">FAQ</div>
          <h2 className="text-3xl md:text-4xl lg:text-[44px] font-bold text-white tracking-[-0.025em] leading-[1.1]">
            Common questions
          </h2>
        </div>

        <div ref={elementRef} className="max-w-3xl divide-y divide-white/[0.07] border-y border-white/[0.07]" style={getRevealStyle(0)}>
          {faqs.map((faq, index) => {
            const isOpen = open === index;
            return (
              <div key={faq.question} className="py-1">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${index}`}
                  className="group w-full flex items-center justify-between gap-6 text-left text-[17px] font-medium text-white py-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                >
                  <span className="flex-1">{faq.question}</span>
                  <Plus
                    className={`w-4 h-4 shrink-0 text-primary transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}
                    strokeWidth={2}
                  />
                </button>
                <div
                  id={`faq-answer-${index}`}
                  className={`overflow-hidden transition-[max-height,opacity] duration-300 ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <p className="text-[15px] text-white/65 leading-relaxed pb-6 pr-10">
                    {faq.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
